<div class="calendar-container">
  <!-- Top toolbar with view toggle, add button, and filter button -->
  <div class="calendar-toolbar">
    <!-- Left side: View Toggle and Add Button -->
    <div class="toolbar-left">
      <!-- View Toggle Button -->
      <div class="view-toggle-container">
        <mat-button-toggle-group
          class="view-toggle-group compact"
          appearance="standard"
          [value]="viewType"
          (change)="toggleView($event)"
          [hideSingleSelectionIndicator]="true"
          aria-label="Skift mellem kalender- og listevisning"
        >
          <mat-button-toggle value="calendar" class="toggle-button">
            <mat-icon>calendar_view_month</mat-icon>
            <span class="toggle-label">Kalender</span>
          </mat-button-toggle>
          <mat-button-toggle value="table" class="toggle-button">
            <mat-icon>list</mat-icon>
            <span class="toggle-label">Liste</span>
          </mat-button-toggle>
        </mat-button-toggle-group>
      </div>

      @if (viewType === 'calendar') {
      <div class="view-toggle-container schedule-view-container">
        <mat-button-toggle-group
          class="view-toggle-group compact schedule-view-toggle"
          appearance="standard"
          [value]="calendarScheduleView"
          (change)="onCalendarScheduleViewChange($event)"
          [hideSingleSelectionIndicator]="true"
          aria-label="Skift mellem dags-, uge- og månedsvisning"
        >
          @for (option of calendarScheduleViewOptions; track option) {
          <mat-button-toggle
            [value]="option.value"
            class="toggle-button"
            [matTooltip]="option.tooltip"
          >
            <mat-icon>{{ option.icon }}</mat-icon>
            <span class="toggle-label">{{ option.label }}</span>
          </mat-button-toggle>
          }
        </mat-button-toggle-group>
      </div>
      }

      <!-- Add Event Button (role-based visibility) -->
      @if (canAddEvents()) {
      <button
        mat-raised-button
        color="primary"
        class="add-event-button"
        (click)="goToAddEvent()"
        aria-label="Tilføj ny begivenhed"
        matTooltip="Tilføj ny begivenhed"
      >
        <mat-icon>add</mat-icon>
        <span class="add-label">Tilføj</span>
      </button>
      }
    </div>

    <!-- Right side: Filter Button -->
    <div class="toolbar-right">
      <!-- Filter Button (only visible in tablet/mobile modes) -->
      <button
        mat-fab
        class="filter-fab"
        [class.has-active-filters]="hasActiveFilters()"
        [class.mobile-mode]="currentMode === 3"
        [class.tablet-mode]="currentMode === 2"
        [class.desktop-mode]="currentMode === 1"
        (click)="openFilterOverlay()"
        aria-label="Toggle filters"
        matTooltip="Filtrer begivenheder"
      >
        <mat-icon>filter_list</mat-icon>
        @if (activeFilterCount > 0) {
        <span class="filter-badge">{{ activeFilterCount }}</span>
        }
      </button>
    </div>
  </div>

  <!-- Main content area -->
  <div class="calendar-layout">
    <!-- Main calendar/table content -->
    <div class="calendar-main-content">
      <!-- Calendar View -->
      <div
        class="calendar-wrapper"
        #calendarWrapper
        (click)="onCalendarWrapperClick($event)"
        [class.tablet-mode]="currentMode === 2"
        [class.mobile-mode]="currentMode === 3"
        [class.loading]="isLoadingData && !isInitialLoading"
        [class.view-hidden]="viewType !== 'calendar'"
      >
        <!-- Subtle loading indicator (only for non-initial loads) -->
        @if (isLoadingData && !isInitialLoading && viewType === 'calendar') {
        <div class="subtle-loading-indicator">
          <div class="loading-spinner-small"></div>
        </div>
        }

        <!-- Full loading overlay (only for initial load) -->
        @if ((isInitialLoading || isFilterLoading) && viewType === 'calendar') {
        <div class="loading-overlay">
          <div class="loading-content">
            <div class="loading-spinner"></div>
            <div class="loading-text">Indlæser...</div>
          </div>
        </div>
        }

        <ejs-schedule
          #scheduleObj
          style="border-radius: 6px"
          class="duna-kalender"
          [class.tablet-mode]="currentMode === 2"
          [class.mobile-mode]="currentMode === 3"
          [class.can-add-events]="canAddEvents()"
          [selectedDate]="selectedDate"
          [firstDayOfWeek]="1"
          [showWeekNumber]="true"
          weekRule="FirstFourDayWeek"
          locale="da"
          [currentView]="calendarScheduleView"
          startHour="06:00"
          endHour="22:00"
          [eventSettings]="eventSettings"
          [readonly]="true"
          [rowAutoHeight]="false"
          (eventClick)="onEventClick($event)"
          (moreEventsClick)="onMoreEventsClick($event)"
          (cellClick)="onCalendarCellClick($event)"
          (popupOpen)="onCalendarPopupOpen($event)"
          (renderCell)="onScheduleRenderCell($event)"
          [showQuickInfo]="false"
          (eventRendered)="onEventRendered($event)"
          (navigated)="onCalendarMonthChange($event)"
          (navigate)="onCalendarMonthChange($event)"
          (actionBegin)="onCalendarActionBegin($event)"
          (actionComplete)="onCalendarActionComplete($event)"
        >
          <e-views>
            <e-view option="Day"></e-view>
            <e-view option="Week"></e-view>
            <e-view option="Month"></e-view>
          </e-views>
        </ejs-schedule>
      </div>

      <!-- Table View -->
      <div
        #tableContainer
        class="table-container"
        [class.view-hidden]="viewType !== 'table'"
      >
        <!-- Date Range Selectors for List View -->
        <div class="date-range-selectors">
          <div class="date-range-controls">
            <div class="date-input-group compact">
              <label for="startDate">Fra</label>
              <ejs-datepicker
                id="startDate"
                class="date-input date-picker-field"
                [value]="listViewStartDateValue"
                locale="da"
                format="dd/MM/yyyy"
                aria-label="Periode fra dato"
                (change)="onListViewStartDateChange($event.value)"
              ></ejs-datepicker>
            </div>
            <div class="date-input-group compact">
              <label for="endDate">Til</label>
              <ejs-datepicker
                id="endDate"
                class="date-input date-picker-field"
                [value]="listViewEndDateValue"
                locale="da"
                format="dd/MM/yyyy"
                aria-label="Periode til dato"
                (change)="onListViewEndDateChange($event.value)"
              ></ejs-datepicker>
            </div>
            <div class="date-preset-chips">
              @for (preset of datePresetButtons; track preset) {
              <button
                type="button"
                class="date-preset-chip"
                (click)="applyDatePreset(preset.id)"
              >
                {{ preset.label }}
              </button>
              }
            </div>
          </div>
        </div>

        <!-- Modern Cards View -->
        <div class="events-cards-container">
          <!-- Empty state: one block so only spinner OR message shows (avoids brief "Ingen begivenheder" flash) -->
          @if (!paginatedCardData.length) {
          <!-- Spinner when loading, before first load, or until we've applied data (avoids flash when loading clears before data is applied) -->
          @if (isLoadingData || isInitialLoading || isFilterLoading ||
          isListViewRefreshing) {
          <div class="cards-loading">
            <div class="loading-content">
              <div class="loading-spinner" aria-hidden="true"></div>
              <p>Indlæser begivenheder...</p>
            </div>
          </div>
          }
          <!-- "Ingen begivenheder" only when the last completed load actually returned 0 events -->
          @if (!isLoadingData && !isInitialLoading && !isFilterLoading &&
          hasLoadedInitialData && lastLoadResultWasEmpty &&
          !isListViewRefreshing) {
          <div class="no-events-message">
            <div class="no-events-content">
              <mat-icon>event_busy</mat-icon>
              <h3>Ingen begivenheder fundet</h3>
              @if (!getCurrentSearchText()) {
              <p>Der er ingen begivenheder i den valgte periode</p>
              } @if (getCurrentSearchText()) {
              <p>Prøv at justere dine søgekriterier</p>
              }
            </div>
          </div>
          } }

          <!-- Cards Grid (hidden while refreshing to avoid showing stale calendar data) -->
          @if (paginatedCardData && paginatedCardData.length > 0 &&
          !isListViewRefreshing) {
          <div class="events-cards-grid">
            @for (event of paginatedCardData; track event) {
            <div class="event-card" (click)="onEventCardClick(event)">
              <div class="card-header">
                <div
                  class="card-icon"
                  [ngClass]="getEventTypeClass(event.event_type)"
                >
                  <mat-icon>{{ getEventTypeIcon(event.event_type) }}</mat-icon>
                </div>
                <div
                  class="card-type-badge"
                  [ngClass]="getEventTypeClass(event.event_type)"
                >
                  {{ getEventTypeName(event.event_type) }}
                </div>
              </div>
              <div class="card-content">
                <h3 class="card-title">{{ event.title || event.name }}</h3>
                <div class="card-meta">
                  <!-- Combined Date and Time -->
                  <div class="meta-item large">
                    <mat-icon>event</mat-icon>
                    <div class="meta-content">
                      <span class="meta-label">Dato & Tid</span>
                      <span class="meta-value">{{
                        formatEventDateTime(event)
                      }}</span>
                    </div>
                  </div>
                  <!-- Capacity Information (hidden for kurser when org sets events_hide_course_capacity_public) -->
                  @if (getEventCapacity(event) &&
                  showCourseCapacityForEvent(event)) {
                  <div class="capacity-section">
                    <div class="capacity-bar">
                      <div
                        class="capacity-fill"
                        [style.width.%]="getCapacityPercentage(event)"
                      ></div>
                    </div>
                    <div class="capacity-info">
                      <span class="capacity-text">
                        <strong>{{ getRegisteredCount(event) }}</strong> /
                        {{ getEventCapacity(event) }} deltagere
                      </span>
                      <span
                        class="capacity-status"
                        [ngClass]="getCapacityStatusClass(event)"
                      >
                        {{ getCapacityStatusText(event) }}
                      </span>
                    </div>
                  </div>
                  }
                  <!-- Additional Details Grid -->
                  <div class="card-details-grid">
                    @if (event.location_name ||
                    event.predefined_location?.locations_name ||
                    event.manual_location) {
                    <div class="detail-chip">
                      <mat-icon>place</mat-icon>
                      <span>{{
                        event.location_name ||
                          event.predefined_location?.locations_name ||
                          event.manual_location
                      }}</span>
                    </div>
                    } @if (event.organization_name ||
                    event.organization?.org_name) {
                    <div class="detail-chip">
                      <mat-icon>business</mat-icon>
                      <span>{{
                        event.organization_name || event.organization?.org_name
                      }}</span>
                    </div>
                    } @if (event.price && event.price > 0) {
                    <div class="detail-chip price">
                      <mat-icon>payments</mat-icon>
                      <span>{{ event.price }} kr.</span>
                    </div>
                    } @if (!event.price || event.price === 0) {
                    <div class="detail-chip free">
                      <mat-icon>check_circle</mat-icon>
                      <span>Gratis</span>
                    </div>
                    } @if (event.catering && event.catering !== 'self_catering')
                    {
                    <div class="detail-chip catering">
                      <mat-icon>restaurant</mat-icon>
                      <span>{{ getCateringText(event.catering) }}</span>
                    </div>
                    } @if (shouldShowVisibilityLabel(event.visibility)) {
                    <div class="detail-chip visibility">
                      <mat-icon>{{
                        getVisibilityIcon(event.visibility)
                      }}</mat-icon>
                      <span>{{ getVisibilityText(event.visibility) }}</span>
                    </div>
                    } @if (getWaitlistCount(event) > 0) {
                    <div class="detail-chip waitlist">
                      <mat-icon>schedule</mat-icon>
                      <span>{{ getWaitlistCount(event) }} på venteliste</span>
                    </div>
                    }
                  </div>
                  <!-- Status badges -->
                  <div class="status-badges">
                    @if (!event.is_active) {
                    <span class="status-badge inactive">
                      <mat-icon>block</mat-icon>
                      Inaktiv
                    </span>
                    } @if (!event.is_shown) {
                    <span class="status-badge hidden">
                      <mat-icon>visibility_off</mat-icon>
                      Skjult
                    </span>
                    }
                  </div>
                </div>
              </div>
              <div class="card-footer">
                <button
                  mat-raised-button
                  color="primary"
                  (click)="onEventCardClick(event); $event.stopPropagation()"
                >
                  <mat-icon>visibility</mat-icon>
                  Vis detaljer
                </button>
              </div>
            </div>
            }
          </div>
          }

          <!-- Custom Pagination -->
          @if (filteredCardData && filteredCardData.length > 0 &&
          !isListViewRefreshing) {
          <div class="cards-pagination">
            <div class="pagination-info">
              <span class="pagination-text">
                Viser {{ cardPageIndex * cardPageSize + 1 }} -
                {{
                  Math.min(
                    (cardPageIndex + 1) * cardPageSize,
                    filteredCardData.length
                  )
                }}
                af {{ filteredCardData.length }} begivenheder
              </span>
            </div>
            <div class="pagination-controls">
              <!-- Page size selector -->
              <div class="page-size-selector">
                <label for="cardPageSizeSelect">Per side:</label>
                <select
                  id="cardPageSizeSelect"
                  [(ngModel)]="cardPageSize"
                  (change)="onCardPageSizeChange()"
                  class="custom-select"
                >
                  <option [value]="6">6</option>
                  <option [value]="12">12</option>
                  <option [value]="24">24</option>
                  <option [value]="48">48</option>
                </select>
              </div>
              <!-- Navigation buttons -->
              <div class="pagination-navigation">
                <button
                  mat-icon-button
                  [disabled]="cardPageIndex === 0"
                  (click)="onCardPageChange(cardPageIndex - 1)"
                  class="nav-button"
                >
                  <mat-icon>chevron_left</mat-icon>
                </button>
                <span class="page-info">
                  Side {{ cardPageIndex + 1 }} af {{ getCardTotalPages() }}
                </span>
                <button
                  mat-icon-button
                  [disabled]="cardPageIndex >= getCardTotalPages() - 1"
                  (click)="onCardPageChange(cardPageIndex + 1)"
                  class="nav-button"
                >
                  <mat-icon>chevron_right</mat-icon>
                </button>
              </div>
            </div>
          </div>
          }
        </div>
      </div>
    </div>

    <!-- Filter Sidebar (Desktop only) -->
    <div
      class="calendar-filter-sidebar"
      [class.desktop-mode]="currentMode === 1"
      [class.tablet-mode]="currentMode === 2"
      [class.mobile-mode]="currentMode === 3"
    >
      <div class="filter-sidebar-content">
        <app-duna-events-filter
          [filterKey]="'events-filter'"
          [settings]="{
            showSearch: true,
            showEventTypes: true,
            showOrganizations: true,
            showResultCount: true,
            showResetButton: true,
            title: 'Filtrer begivenheder'
          }"
        >
        </app-duna-events-filter>
      </div>
    </div>
  </div>
</div>

<!-- Sliding Filter Panel (desktop only) -->
@if (isFilterPanelOpen && currentMode === 1) {
<div
  class="filter-panel-container desktop-mode"
  role="dialog"
  aria-labelledby="filter-panel-title"
  aria-modal="true"
  (click)="closeFilterPanel()"
>
  <div class="filter-panel" (click)="$event.stopPropagation()">
    <div class="filter-panel-header">
      <h3 id="filter-panel-title">Filtrer begivenheder</h3>
      <button
        mat-icon-button
        (click)="closeFilterPanel(); $event.stopPropagation()"
        aria-label="Luk filter panel"
        title="Luk filter panel"
      >
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div
      class="filter-panel-content"
      role="main"
      (click)="$event.stopPropagation()"
    >
      <app-duna-events-filter
        [filterKey]="'events-filter'"
        [settings]="{
          showSearch: true,
          showEventTypes: true,
          showOrganizations: true,
          showResultCount: true,
          showResetButton: true,
          title: 'Filter'
        }"
      >
      </app-duna-events-filter>
    </div>
  </div>
</div>
}
